import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  TextField,
  Grid,
  styled,
  CircularProgress,
  Typography,
  IconButton,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Clear } from '@mui/icons-material';
import UploadIcon from '@mui/icons-material/Upload';
import { createCompanyIssuer, createIssuerByAdmin, getCompany, inviteCompanyIssuerByEmail, inviteIssuerByEmail, validateEmail } from '../../../services/apiservice';
import { useAuth } from '../../../contexts/authContext';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { Helmet } from 'react-helmet-async';
import { debounce } from 'lodash';

const RedAsterisk = styled('span')({
  color: 'red',
});
const FileInputWrapper = styled('label')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
});

const UploadInput = styled('input')({
  display: 'none',
});

const UploadText = styled('span')(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.primary,
}));


function CompanyIssuerForm({ companyDetails }) {
  const { user } = useAuth();
  const issuerID = user.IssuerID;
  const [imageName, setImageName] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [company, setCompany] = useState(companyDetails);
  const [inviteEmail, setInviteEmail] = useState('');
  const [emailValidationError, setEmailValidationError] = useState(null);
  const [emailValidationErrorInDialogBox, setEmailValidationErrorInDialogBox] = useState(null);


  const validationSchema = Yup.object({
    IssuerName: Yup.string().required('Company Name is required'),
    IssuerRepFirstName: Yup.string().required('First Name is required'),
    IssuerRepLastName: Yup.string().required('Last Name is required'),
    // IssuerRepEmailID: Yup.string()
    // .email('Invalid email format')
    // .required('Email is required'),
    // IssuerRepEmailID: Yup.string()
    // .email('Invalid email address')
    // .required('Email is required')
    // .test('email-validation', 'Invalid representative email address. Please provide a valid email.', function(value) {
    //   return !emailValidationError;
    // }),
    IssuerRepEmailID: Yup.string().email('Invalid email format').required('Email is required'),
    IssuerRepPhoneNumber: Yup.string().nullable(),
    IssuerWebsite: Yup.string().url('Enter a valid URL').nullable(),
    IssuerLogoURL: Yup.mixed()
      .nullable()
      .test('fileSize', 'Image size is too large, max size is 2MB', (value) => !value || (value && value.size <= 2 * 1024 * 1024))
      .test('fileType', 'Only image formats are allowed (jpg, png, jpeg)', (value) => !value || (value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type))),
  });

  const InviteEmailSchema = Yup.object().shape({
    IssuerRepEmailID: Yup.string().email('Invalid email format').required('Email is required'),
    // IssuerRepEmailID: Yup.string()
    // .email('Invalid email address')
    // .required('Email is required')
    // .test('email-validation', 'Invalid invite email address. Please provide a valid email.', function(value) {
    //   return !emailValidationErrorInDialogBox;
    // }),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClearImage = (setFieldValue) => {
    setImageName(null);
    setFieldValue('IssuerLogoURL', null);
  };

  const handleInviteIssuer = async (email) => {
    try {
      const inviteResponse = await inviteCompanyIssuerByEmail(email, issuerID);
      if (inviteResponse.success === true) {
        setSnackbarMessage('Invite sent successfully!');
        setSnackbarSeverity('success');
        setDialogOpen(false);
        setInviteEmail('');
        setEmailValidationErrorInDialogBox(null)
      } else {
        setSnackbarMessage('Issuer already exists');
        setSnackbarSeverity('error');
        setInviteEmail('');
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Invite error:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // const debouncedCheckEmailValidationMain = useMemo(
  //   () =>
  //     debounce(async (email) => {
  //       if (!email) {
  //         setEmailValidationError(null);
  //         return;
  //       }
  //       try {
  //         const response = await validateEmail(email);
  //         if (!response) {
  //           setEmailValidationError('Invalid representative email address. Please provide a valid email.');
  //         } else {
  //           setEmailValidationError(null);
  //         }
  //       } catch (error) {
  //         console.error('Error checking email validation:', error);
  //       }
  //     }, 300),
  //   [setEmailValidationError]
  // );

  // const debouncedCheckEmailValidationDialog = useMemo(
  //   () =>
  //     debounce(async (email) => {
  //       if (!email) {
  //         setEmailValidationErrorInDialogBox(null);
  //         return;
  //       }
  //       try {
  //         const response = await validateEmail(email);
  //         if (!response) {
  //           setEmailValidationErrorInDialogBox('Invalid invite email address. Please provide a valid email.');
  //         } else {
  //           setEmailValidationErrorInDialogBox(null);
  //         }
  //       } catch (error) {
  //         console.error('Error checking email validation:', error);
  //       }
  //     }, 300),
  //   [setEmailValidationErrorInDialogBox]
  // );


  // useEffect(() => {
  //   return () => {
  //     debouncedCheckEmailValidationMain.cancel();
  //     debouncedCheckEmailValidationDialog.cancel();
  //   };
  // }, [debouncedCheckEmailValidationMain, debouncedCheckEmailValidationDialog]);

  const handleCloseDialog = (resetForm) => {
    setDialogOpen(false);
    setInviteEmail('');
    setEmailValidationErrorInDialogBox(null);
    resetForm();
  };

  return (
    <>
      <Helmet>
        <title>Issuer Form</title>
      </Helmet>
      <SnackbarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} style={{ marginTop: '50px' }} />

      <Dialog
        open={dialogOpen}
        onClose={() => handleCloseDialog()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            margin: 'auto',
          },
        }}
      >
        <DialogTitle>Send Invite</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{ IssuerRepEmailID: inviteEmail }}
            validationSchema={InviteEmailSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await handleInviteIssuer(values.IssuerRepEmailID);
                setInviteEmail(values.IssuerRepEmailID);
              } finally {
                setSubmitting(false);
              }
            }}
            enableReinitialize
          >
            {({ errors, touched, isSubmitting, values, setFieldValue, resetForm }) => (
              <Form>
                <Field
                  as={TextField}
                  label="Company Name"
                  name="IssuerName"
                  value={company ? company.CompanyName : ''}
                  fullWidth
                  disabled
                  style={{ marginTop: 16 }}
                />
                <Field
                  as={TextField}
                  label="Issuer Email"
                  name="IssuerRepEmailID"
                  fullWidth
                  error={touched.IssuerRepEmailID && Boolean(errors.IssuerRepEmailID) || emailValidationErrorInDialogBox}
                  helperText={(touched.IssuerRepEmailID && errors.IssuerRepEmailID) || emailValidationErrorInDialogBox}
                  onChange={(e) => {
                    const email = e.target.value;
                    setFieldValue('IssuerRepEmailID', email);
                    // const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                    // isCompleteEmail && debouncedCheckEmailValidationDialog(email); 
                  }}
                  style={{ marginTop: 16 }}
                  InputProps={{
                    endAdornment: (
                      <>
                        {values.IssuerRepEmailID && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setFieldValue('IssuerRepEmailID', '')}
                              edge="end"
                              aria-label="clear email"
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }}
                />
                <DialogActions>
                  <Button onClick={() => handleCloseDialog(resetForm)} color="secondary">
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={15} color="inherit" /> : null}>
                    {isSubmitting ? 'Sending Invite...' : 'Send Invite'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>


      <Formik
        initialValues={{
          IssuerName: company ? company.CompanyID : '',
          IssuerRepFirstName: '',
          IssuerRepLastName: '',
          IssuerRepEmailID: '',
          IssuerRepPhoneNumber: '',
          IssuerWebsite: '',
          IssuerLogoURL: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
              const value = values[key];
              if (value !== '' && value !== null) {
                formData.append(key, value);
              }
            });

            const response = await createCompanyIssuer(formData, issuerID);
            if (response.success) {
              setSnackbarMessage('Issuer created successfully!');
              setSnackbarSeverity('success');
              resetForm();
            } else {
              setSnackbarMessage('Failed to create Issuer.');
              setSnackbarSeverity('error');
            }
            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, setFieldValue, isSubmitting }) => (
          <Card sx={{ width: '100%', padding: 2 }}>
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Company Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="IssuerName"
                    value={company ? company.CompanyName : ''}
                    fullWidth
                    error={touched.IssuerName && Boolean(errors.IssuerName)}
                    helperText={touched.IssuerName && errors.IssuerName}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Representative First Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="IssuerRepFirstName"
                    fullWidth
                    error={touched.IssuerRepFirstName && Boolean(errors.IssuerRepFirstName)}
                    helperText={touched.IssuerRepFirstName && errors.IssuerRepFirstName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Representative Last Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="IssuerRepLastName"
                    fullWidth
                    error={touched.IssuerRepLastName && Boolean(errors.IssuerRepLastName)}
                    helperText={touched.IssuerRepLastName && errors.IssuerRepLastName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Representative Email'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="IssuerRepEmailID"
                    type="email"
                    fullWidth
                    error={touched.IssuerRepEmailID && Boolean(errors.IssuerRepEmailID) || emailValidationError}
                    helperText={(touched.IssuerRepEmailID && errors.IssuerRepEmailID) || emailValidationError}
                    onChange={(e) => {
                      const email = e.target.value;
                      setFieldValue('IssuerRepEmailID', email);
                      // const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                      // isCompleteEmail && debouncedCheckEmailValidationMain(email);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Representative Phone Number'}
                      </>
                    }
                    name="IssuerRepPhoneNumber"
                    fullWidth
                    error={touched.IssuerRepPhoneNumber && Boolean(errors.IssuerRepPhoneNumber)}
                    helperText={touched.IssuerRepPhoneNumber && errors.IssuerRepPhoneNumber}
                  />
                </Grid>
                {/* <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label="Website"
                    name="IssuerWebsite"
                    fullWidth
                    error={touched.IssuerWebsite && Boolean(errors.IssuerWebsite)}
                    helperText={touched.IssuerWebsite && errors.IssuerWebsite}
                  />
                </Grid> */}
                <Grid item xs={3}>
                  <FileInputWrapper>
                    <UploadInput
                      accept="image/*"
                      id="IssuerLogoURL"
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setImageName(file ? file.name : null);
                        setFieldValue('IssuerLogoURL', file);
                      }}
                    />
                    <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                      Upload Company Logo
                    </Button>
                  </FileInputWrapper>
                  <UploadText style={{ marginLeft: '8px' }}>
                    {imageName ? (
                      <>
                        {imageName}
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClearImage(setFieldValue);
                          }}
                          size="small"
                          color="error"
                          aria-label="clear"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      'No file chosen'
                    )}
                  </UploadText>
                  {errors.IssuerLogoURL && (
                    <Typography color="error">{errors.IssuerLogoURL}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isSubmitting ? 'Creating Issuer...' : 'Create Issuer'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setDialogOpen(true)}
                    style={{ marginLeft: 16 }}
                  >
                    Invite Issuer
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Card>
        )}
      </Formik>
    </>
  );
}

export default CompanyIssuerForm;
